import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import HonestyMeter from "@/components/HonestyMeter";
import { useAuth } from "@/lib/AuthContext";
import { LogIn } from "lucide-react";

const CATS = ["Politics", "Business", "Travel", "Automotive", "Fashion", "Tech", "Food", "Books", "Sports", "Opinion"];

function AdminLoginGate() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="max-w-[560px] mx-auto px-6 md:px-10 py-16 fade-up" data-testid="admin-login-gate">
      <div className="tag-red inline-block">Editor's Desk</div>
      <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mt-3">Sign in to publish.</h1>
      <p className="font-body mt-4 opacity-80">
        The admin panel is protected. Sign in with Google — Emergent handles the auth flow, we never see your password.
      </p>
      <button onClick={login} className="btn-ink mt-8 inline-flex items-center gap-2" data-testid="admin-google-login">
        <LogIn size={16} /> Sign in with Google
      </button>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  if (loading) return <div className="max-w-[560px] mx-auto p-16 font-quote text-2xl">Checking auth…</div>;
  if (!user) return <AdminLoginGate />;
  return <AdminPanel />;
}

function AdminPanel() {
  const { user, isAdmin } = useAuth();
  const [form, setForm] = useState({
    text: "", speaker: "", speaker_role: "", source_url: "",
    source_type: "article", year: new Date().getFullYear(),
    category: "Opinion", featured: false, image_url: "", tags: "",
  });
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const [subs, setSubs] = useState([]);
  const [recent, setRecent] = useState([]);
  const [subCount, setSubCount] = useState(0);
  const [ingestRuns, setIngestRuns] = useState([]);
  const [ingesting, setIngesting] = useState(false);
  const [sending, setSending] = useState(false);

  const loadSubs = () => api.get("/submissions").then(r => setSubs(r.data)).catch(() => {});
  const loadRecent = () => api.get("/quotes", { params: { limit: 8 } }).then(r => setRecent(r.data));
  const loadSubCount = () => api.get("/subscribers/count").then(r => setSubCount(r.data.count)).catch(() => {});
  const loadIngestRuns = () => api.get("/admin/ingest-runs").then(r => setIngestRuns(r.data)).catch(() => {});
  useEffect(() => { loadSubs(); loadRecent(); loadSubCount(); loadIngestRuns(); }, []);

  const analyze = async () => {
    if (!form.text.trim()) { toast.error("Add a quote first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/quotes/analyze", {
        text: form.text, speaker: form.speaker, context: form.speaker_role,
      });
      setAi(data);
      if (data.suggested_categories?.length) {
        const first = data.suggested_categories[0];
        if (CATS.includes(first)) setForm(f => ({ ...f, category: first }));
      }
      toast.success("Analysed.");
    } catch {
      toast.error("AI unavailable.");
    } finally { setBusy(false); }
  };

  const publish = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form,
        year: parseInt(form.year, 10) || undefined,
        tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
      };
      await api.post("/quotes", body);
      toast.success("Published to the archive.");
      setForm({ ...form, text: "", speaker: "", speaker_role: "", source_url: "", image_url: "", tags: "" });
      setAi(null);
      loadRecent();
    } catch {
      toast.error("Publish failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 fade-up">
      <div className="tag-red mb-3 inline-block">Editor's Desk</div>
      <h1 className="font-display text-5xl md:text-6xl leading-[0.95]">Add to the record.</h1>
      <div className="mt-3 font-mono text-xs opacity-70" data-testid="admin-user-info">
        Signed in as <b>{user?.email}</b>{isAdmin ? "" : " — you can publish quotes but ingest/send-weekly need an allow-listed admin email."}
      </div>
      <p className="font-body mt-3 max-w-2xl">Paste the quote, hit analyse, review the honesty score, publish.</p>

      <div className="grid grid-cols-12 gap-8 mt-10">
        <form onSubmit={publish} className="col-span-12 lg:col-span-8 space-y-5 border-ink bg-white p-6 md:p-8 shadow-brut-sm" data-testid="admin-form">
          <div>
            <label className="font-ui text-xs block mb-2">Quote *</label>
            <textarea required rows={3} value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              className="border-ink bg-[#F4F4F0] px-4 py-3 font-quote text-lg w-full"
              data-testid="admin-text" />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="font-ui text-xs block mb-2">Speaker *</label>
              <input required value={form.speaker}
                onChange={(e) => setForm({ ...form, speaker: e.target.value })}
                className="border-ink bg-[#F4F4F0] px-4 py-3 w-full" data-testid="admin-speaker" />
            </div>
            <div>
              <label className="font-ui text-xs block mb-2">Role</label>
              <input value={form.speaker_role}
                onChange={(e) => setForm({ ...form, speaker_role: e.target.value })}
                className="border-ink bg-[#F4F4F0] px-4 py-3 w-full" data-testid="admin-role" />
            </div>
            <div>
              <label className="font-ui text-xs block mb-2">Source URL</label>
              <input type="url" value={form.source_url}
                onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                className="border-ink bg-[#F4F4F0] px-4 py-3 w-full" data-testid="admin-source" />
            </div>
            <div>
              <label className="font-ui text-xs block mb-2">Source type</label>
              <select value={form.source_type}
                onChange={(e) => setForm({ ...form, source_type: e.target.value })}
                className="border-ink bg-[#F4F4F0] px-4 py-3 w-full" data-testid="admin-source-type">
                {["article", "video", "talk", "tweet", "podcast"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="font-ui text-xs block mb-2">Year</label>
              <input type="number" value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                className="border-ink bg-[#F4F4F0] px-4 py-3 w-full" data-testid="admin-year" />
            </div>
            <div>
              <label className="font-ui text-xs block mb-2">Category</label>
              <select value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="border-ink bg-[#F4F4F0] px-4 py-3 w-full" data-testid="admin-category">
                {CATS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="font-ui text-xs block mb-2">Image URL</label>
              <input value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                className="border-ink bg-[#F4F4F0] px-4 py-3 w-full" data-testid="admin-image" />
            </div>
            <div className="md:col-span-2">
              <label className="font-ui text-xs block mb-2">Tags (comma-separated)</label>
              <input value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                className="border-ink bg-[#F4F4F0] px-4 py-3 w-full" data-testid="admin-tags" />
            </div>
            <label className="md:col-span-2 flex items-center gap-2 font-ui text-xs">
              <input type="checkbox" checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                data-testid="admin-featured" />
              Feature on homepage
            </label>
          </div>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-[#0A0A0A]/20">
            <button type="button" onClick={analyze} disabled={busy} className="btn-outline" data-testid="admin-analyze-btn">
              {busy ? "Working…" : "Analyse with AI"}
            </button>
            <button type="submit" disabled={busy} className="btn-ink" data-testid="admin-publish-btn">
              {busy ? "…" : "Publish"}
            </button>
          </div>
        </form>

        <aside className="col-span-12 lg:col-span-4 space-y-6">
          <div className="border-ink bg-white p-6 shadow-brut-sm">
            <div className="font-ui text-xs text-[#FF3333]">AI Preview</div>
            <h3 className="font-display text-2xl mt-2">Honesty analysis</h3>
            {ai ? (
              <div className="mt-4 space-y-3" data-testid="ai-preview">
                <HonestyMeter score={ai.honesty_score} />
                <div>
                  <div className="font-ui text-[10px] opacity-70">Rationale</div>
                  <p className="font-body text-sm">{ai.rationale}</p>
                </div>
                <div>
                  <div className="font-ui text-[10px] opacity-70">Summary</div>
                  <p className="font-quote text-base">{ai.summary}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ai.suggested_categories?.map(c => <span key={c} className="pill">{c}</span>)}
                </div>
              </div>
            ) : (
              <p className="font-body text-sm opacity-70 mt-3">Run analysis to preview.</p>
            )}
          </div>

          <div className="border-ink bg-white p-6 shadow-brut-sm">
            <div className="font-ui text-xs text-[#FF3333]">Community · {subs.length}</div>
            <h3 className="font-display text-xl mt-2">Submissions queue</h3>
            <div className="mt-3 space-y-3 max-h-60 overflow-auto" data-testid="submissions-list">
              {subs.slice(0, 8).map(s => (
                <div key={s.id} className="border-t border-[#0A0A0A]/20 pt-2 text-sm">
                  <div className="font-quote italic">"{s.text.slice(0, 90)}…"</div>
                  <div className="font-mono text-[10px] opacity-70">{s.speaker}</div>
                </div>
              ))}
              {subs.length === 0 && <p className="font-body text-xs opacity-70">Nothing pending.</p>}
            </div>
          </div>

          <div className="border-ink bg-[#0A0A0A] text-[#F4F4F0] p-6 shadow-brut-sm" data-testid="admin-ingest-panel">
            <div className="font-ui text-xs text-[#FF3333]">Ingestion Pipeline</div>
            <h3 className="font-display text-xl mt-2">Auto-scraper</h3>
            <p className="font-body text-xs opacity-80 mt-2">
              Runs every 60m generating AI-ILLUSTRATED entries (clearly labelled). Wire a SerpAPI/YouTube key to switch to real news scraping.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={async () => {
                  setIngesting(true);
                  try {
                    const { data } = await api.post("/admin/ingest-now");
                    toast.success(`Added ${data.added} entries.`);
                    loadIngestRuns(); loadRecent();
                  } catch { toast.error("Ingest failed."); }
                  finally { setIngesting(false); }
                }}
                disabled={ingesting}
                className="btn-outline !text-[#F4F4F0] !border-[#F4F4F0] text-xs"
                data-testid="admin-ingest-now"
              >
                {ingesting ? "…" : "Run Now (3)"}
              </button>
              <button
                onClick={async () => {
                  setIngesting(true);
                  try {
                    const { data } = await api.post("/admin/generate-bulk?count=30", null, { timeout: 300000 });
                    toast.success(`Added ${data.added} of ${data.requested} entries.`);
                    loadIngestRuns(); loadRecent();
                  } catch { toast.error("Bulk generate failed."); }
                  finally { setIngesting(false); }
                }}
                disabled={ingesting}
                className="btn-ink !bg-[#FF3333] !border-[#FF3333] text-xs"
                data-testid="admin-generate-bulk"
              >
                Bulk Generate 30
              </button>
            </div>
            <div className="mt-4 space-y-2 max-h-32 overflow-auto" data-testid="admin-ingest-runs">
              {ingestRuns.slice(0, 5).map(r => (
                <div key={r.id} className="font-mono text-[10px] opacity-80 border-t border-[#333] pt-2 flex justify-between">
                  <span>+{r.added} · {r.mode}</span>
                  <span>{new Date(r.at).toLocaleTimeString()}</span>
                </div>
              ))}
              {ingestRuns.length === 0 && <p className="font-mono text-[10px] opacity-60">No runs yet.</p>}
            </div>
          </div>

          <div className="border-ink bg-white p-6 shadow-brut-sm" data-testid="admin-newsletter-panel">
            <div className="font-ui text-xs text-[#FF3333]">Newsletter · {subCount}</div>
            <h3 className="font-display text-xl mt-2">Weekly Report</h3>
            <p className="font-body text-xs opacity-80 mt-2">
              {subCount} subscriber{subCount === 1 ? "" : "s"}. Set RESEND_API_KEY in backend/.env to send.
            </p>
            <button
              onClick={async () => {
                setSending(true);
                try {
                  const { data } = await api.post("/admin/send-weekly");
                  if (data.note) toast.info(`Dry-run: ${data.would_send} would receive.`);
                  else toast.success(`Sent to ${data.sent} / ${data.total_subscribers}.`);
                } catch { toast.error("Send failed."); }
                finally { setSending(false); }
              }}
              disabled={sending}
              className="mt-4 btn-outline w-full text-xs"
              data-testid="admin-send-weekly"
            >
              {sending ? "Sending…" : "Send Weekly Digest"}
            </button>
          </div>
        </aside>
      </div>

      <div className="mt-16">
        <h2 className="font-display text-3xl mb-4">Recently filed</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {recent.map(r => (
            <div key={r.id} className="border-ink bg-white p-4 flex items-start gap-3" data-testid={`admin-recent-${r.id}`}>
              <div className="tag-red">{r.category}</div>
              <div className="flex-1">
                <div className="font-quote text-sm">"{r.text.slice(0, 100)}…"</div>
                <div className="font-mono text-[10px] opacity-70 mt-1">{r.speaker} · {r.honesty_score}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
